import React from 'react';
import Icon from './Icon';
import { Pill, Rating } from './Primitives';

const fallbackAvatar = (name = '') =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=e0e7ff&color=4338ca&size=160&font-size=0.4&bold=true`;

/**
 * Shared doctor card (Find Doctors results, Top Rated / Featured doctors).
 * Presentational only — callers own navigation and booking.
 *
 *   doctor        – doctor object from doctorsApi / ratingsApi
 *   online        – show the green "online" presence dot on the photo
 *   availability  – availability line (node), e.g. "Online Consultation in next 30 mins"
 *   location      – clinic / distance line (node)
 *   onView        – "View Details" (and whole-card click when `clickable`)
 *   onBook        – primary "Book Appointment" CTA
 */
const DoctorCard = ({
  doctor, online, availability, location, featured, clickable,
  onView, onBook, bookLabel = 'Book Appointment',
}) => {
  const dept = doctor.departmentLabel || doctor.department;
  const fee  = doctor.fee ?? doctor.onlineFee;
  const exp  = doctor.yearsExperience ?? doctor.experience;

  return (
    <div
      className={`ds-doc${featured ? ' ds-doc--featured' : ''}${clickable ? ' ds-doc--clickable' : ''}`}
      onClick={clickable ? onView : undefined}
    >
      <div className="ds-doc-top">
        <div className="ds-doc-photo">
          <img
            src={doctor.photoUrl || fallbackAvatar(doctor.name)}
            alt={doctor.name}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackAvatar(doctor.name); }}
          />
          {online && <span className="ds-doc-online" aria-label="Online" />}
        </div>
        <div className="ds-doc-info">
          <div className="ds-doc-name">{doctor.name}</div>
          {doctor.qualifications && <div className="ds-doc-sub">{doctor.qualifications}</div>}
          <Rating rating={doctor.rating} reviewCount={doctor.reviewCount} />
        </div>
      </div>

      <div className="ds-doc-meta">
        {dept && <Pill tone="violet">{dept}</Pill>}
        {exp != null && (
          <span className="ds-doc-meta-item">
            <Icon name="briefcase" size={13} /> {exp} yrs experience
          </span>
        )}
      </div>

      {(availability || location) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {availability && (
            <span className="ds-doc-avail"><span className="ds-dot" />{availability}</span>
          )}
          {location && (
            <span className="ds-doc-meta-item" style={{ fontSize: 12, color: 'var(--ds-text-2)' }}>
              <Icon name="mapPin" size={13} /> {location}
            </span>
          )}
        </div>
      )}

      <div className="ds-doc-foot">
        {fee != null ? (
          <div className="ds-doc-fee">
            ₹{Number(fee).toLocaleString('en-IN')} <small>/ consult</small>
          </div>
        ) : <span />}
        <div className="ds-doc-actions">
          {onView && !clickable && (
            <button type="button" className="ds-btn ds-btn--ghost ds-btn--sm" onClick={onView}>
              Details
            </button>
          )}
          <button
            type="button"
            className="ds-btn ds-btn--sm"
            onClick={(e) => { e.stopPropagation(); onBook(); }}
          >
            {bookLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoctorCard;
